const pad = (value) => String(value).padStart(2, "0");

export const formatDateInputValue = (date) => {
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  return `${year}-${month}-${day}`;
};

export const getPresetDateRange = (preset, now = new Date()) => {
  const end = new Date(now);
  const start = new Date(now);

  if (preset === "today") {
    return {
      from: formatDateInputValue(start),
      to: formatDateInputValue(end),
    };
  }

  if (preset === "last7") {
    start.setDate(start.getDate() - 6);
  } else if (preset === "last30") {
    start.setDate(start.getDate() - 29);
  } else if (preset === "month") {
    start.setDate(1);
  }

  return {
    from: formatDateInputValue(start),
    to: formatDateInputValue(end),
  };
};

export const getDefaultReportingFilters = () => ({
  storeIds: [],
  from: getPresetDateRange("today").from,
  to: getPresetDateRange("today").to,
});

const isSameLocalDate = (value, date) => {
  if (!value || !(date instanceof Date) || Number.isNaN(date.getTime())) {
    return false;
  }
  return value === formatDateInputValue(date);
};

const toLocalBoundaryIso = (value, endOfDay = false, options = {}) => {
  if (!value) return null;
  const { capTodayAtNow = false, now = new Date() } = options;
  if (endOfDay && capTodayAtNow && isSameLocalDate(value, now)) {
    return now.toISOString();
  }
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return null;
  const date = new Date(
    year,
    month - 1,
    day,
    endOfDay ? 23 : 0,
    endOfDay ? 59 : 0,
    endOfDay ? 59 : 0,
    endOfDay ? 999 : 0,
  );
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

export const getUtcRangeFromDateInputs = ({ from, to }, options = {}) => {
  return {
    from: toLocalBoundaryIso(from),
    to: toLocalBoundaryIso(to, true, options),
  };
};

export const formatCurrency = (amount, currencyCode = "USD") => {
  const value = Number(amount || 0);
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode || "USD",
      maximumFractionDigits: 2,
    }).format(value);
  } catch (error) {
    return `${currencyCode || "USD"} ${value.toFixed(2)}`;
  }
};

export const formatNumber = (value) =>
  new Intl.NumberFormat("en-US").format(Number(value || 0));

export const formatDateTime = (value) => {
  if (!value) {
    return "-";
  }

  try {
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }).format(new Date(value));
  } catch (error) {
    return value;
  }
};

export const formatPercent = (value) => `${Number(value || 0).toFixed(1)}%`;

export const getStoreOptionsFromBusinesses = (businesses) => {
  const businessList = getBusinessesListFromResponse(businesses);

  return businessList.flatMap((business) =>
    Array.isArray(business.stores)
      ? business.stores.map((store) => ({
          id: store.storeId || store.id,
          name: store.storeName || store.name,
          businessName: business.businessName || business.name,
        }))
      : [],
  );
};

export const getBusinessesListFromResponse = (businesses) =>
  Array.isArray(businesses)
    ? businesses
    : Array.isArray(businesses?.businesses)
      ? businesses.businesses
      : Array.isArray(businesses?.data)
        ? businesses.data
        : Array.isArray(businesses?.items)
          ? businesses.items
          : [];

export const getUniqueCurrencyCode = (
  ordersOrStores = [],
  fallback = "USD",
) => {
  const codes = [
    ...new Set(
      ordersOrStores.map((entry) => entry?.currencyCode).filter(Boolean),
    ),
  ];
  return codes.length === 1 ? codes[0] : fallback;
};
